import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { tryParseJson } from '@frontend-site/shared/utils';
import { LocalStoreWithExpirationData } from './local-store-with-expiration-data.interface';

export type LocalStoreData = string | number | Date | object;
export interface LocaStorageConfig {
    prefix: string;
}
export const LOCAL_STORAGE_CONFIG = new InjectionToken<LocaStorageConfig>('__LOCAL_STORAGE_PREFIX__');

@Injectable({ providedIn: 'root' })
export class LocalStoreService {
    private _prefix: string;
    private getComposedKey(key: string): string {
        return this._prefix + key;
    }

    constructor(@Inject(LOCAL_STORAGE_CONFIG) @Optional() private readonly config?: LocaStorageConfig) {
        this._prefix = this.config?.prefix ?? '';
        if (this._prefix.substring(-1) !== '.') {
            this._prefix = this._prefix ? this._prefix + '.' : '';
        }
    }

    saveData(key: string, data: LocalStoreData): void {
        if (!key) {
            throw new Error('key cannot be empty');
        }

        this.removeItem(key);
        this.setItemByDataType(key, data);
    }

    getData<T extends LocalStoreData>(key: string, isDate: boolean = false): T | null {
        return this.getItemAsObject(key, isDate);
    }

    removeData(key: string): void {
        let composedKey = this.getComposedKey(key);
        if (localStorage.getItem(composedKey) !== null) {
            localStorage.removeItem(composedKey);
        }
    }

    saveDataWithExpiration(key: string, data: LocalStoreData, ttlMs: number): void {
        if (!key) {
            throw new Error('key cannot be empty');
        }

        const entry: LocalStoreWithExpirationData<LocalStoreData> = {
            data,
            timestamp: Date.now()
        };

        this.removeItem(key);
        this.setItemByDataType(key, entry);
    }

    getDataWithExpiration<T extends LocalStoreData>(key: string, ttlMs: number, isDate: boolean = false): T | null {
        const entry = this.getData<LocalStoreWithExpirationData<T>>(key, false);

        if (!entry || !entry.timestamp) {
            return null;
        }

        const age = Date.now() - entry.timestamp;

        if (age > ttlMs) {
            this.removeData(key);
            return null;
        }

        if (isDate && entry.data) {
            return new Date(entry.data as string | number) as T;
        }

        return entry.data;
    }

    private setItemByDataType(key: string, data: LocalStoreData): void {
        if (typeof data === 'string') {
            this.setItem(key, data);
        }

        if (typeof data === 'number') {
            this.setItem(key, data.toString());
        }

        if (typeof data === 'object') {
            this.setItem(key, JSON.stringify(data));
        }
    }

    private setItem(key: string, data: string): void {
        const composedKey = this.getComposedKey(key);
        localStorage.setItem(composedKey, data);
    }

    private getItem(key: string): LocalStoreData {
        return tryParseJson(localStorage.getItem(this.getComposedKey(key)));
    }
    private getItemAsObject<T extends LocalStoreData>(key: string, isDate: boolean = false): T | null {
        let data = this.getItem(key);
        if (data === null) {
            return null;
        }

        if (isDate) {
            data = new Date(data as string | number);
        }
        return data as T;
    }
    private removeItem(key: string): void {
        localStorage.removeItem(this.getComposedKey(key));
    }
}
